import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  Wallet,
  CheckCircle,
  Zap
} from 'lucide-react';
import Button from '../../components/ui/Button';
import PageHeader from '../../components/ui/PageHeader';
import { useApp } from '../../context/AppContext';
import { useBroker } from '../../context/BrokerContext';
import './PaymentDetails.css';

function PaymentDetails() {
  const navigate = useNavigate();
  const { currentTransaction, setCurrentTransaction, addTransaction, generateReferenceNumber } = useApp();
  const { refPrefix, getBrokerPath } = useBroker();
  const [amount, setAmount] = useState('');
  const [errors, setErrors] = useState({});
  const [isGenerating, setIsGenerating] = useState(false);

  // Redirect if no transaction or not OTP verified
  useEffect(() => {
    if (!currentTransaction || !currentTransaction.otpVerified) {
      navigate('/');
    }
  }, [currentTransaction, navigate]);

  const formatAmount = (value) => {
    const num = value.replace(/\D/g, '');
    return num ? parseInt(num).toLocaleString() : '';
  };

  const handleAmountChange = (e) => {
    const value = e.target.value.replace(/,/g, '');
    if (value === '' || /^\d+$/.test(value)) {
      setAmount(value);
      setErrors({});
    }
  };

  const handleContinue = async () => {
    if (!amount || parseInt(amount) < 100) {
      setErrors({ amount: 'Minimum amount is PKR 100' });
      return;
    }

    setIsGenerating(true);

    // Generate reference number (async - persists to Firebase)
    // Format: PREFIX-DDMMYYYY-SerialNumber (SV for Savexa, JM for JustMarkets)
    const refNumber = await generateReferenceNumber(refPrefix);
    const amountValue = parseInt(amount);

    // Create transaction ID
    const transactionId = `TXN-${Date.now()}`;

    // Update current transaction with reference and amount
    const updatedTransaction = {
      ...currentTransaction,
      id: transactionId,
      referenceNumber: refNumber,
      amount: amountValue,
      status: 'pending_payment',
      createdAt: new Date().toISOString(),
    };

    setCurrentTransaction(updatedTransaction);

    // Add to transactions list
    addTransaction(updatedTransaction);

    setIsGenerating(false);

    // Navigate to reference number page (broker-specific path)
    navigate(getBrokerPath('/reference-number'));
  };

  if (!currentTransaction || !currentTransaction.otpVerified) {
    return null;
  }

  return (
    <div className="payment-container animate-fade-in">
      <div className="payment-main-card">
        {/* Header Section */}
        <PageHeader showBackButton={false} greeting="Enter amount to top-up" />

        {/* Content Section */}
        <div className="payment-content">
          {/* Account Info */}
          <div className="account-info-box">
            <div className="account-icon">
              <CheckCircle size={20} />
            </div>
            <div className="account-details">
              <span className="account-label">Sending from</span>
              <span className="account-title">{currentTransaction.bankAccountTitle}</span>
            </div>
          </div>

          {/* Amount Input Section */}
          <div className="section-header">
            <Wallet size={16} />
            <span>Enter Amount</span>
          </div>

          <div className="amount-input-wrapper">
            <span className="currency-prefix">PKR</span>
            <input
              type="text"
              className="amount-input"
              placeholder="0"
              value={formatAmount(amount)}
              onChange={handleAmountChange}
            />
          </div>
          {errors.amount && (
            <span className="input-error">{errors.amount}</span>
          )}

          {/* Action Button */}
          <div className="action-buttons">
            <Button
              variant="primary"
              size="large"
              fullWidth
              icon={ArrowRight}
              iconPosition="right"
              onClick={handleContinue}
              loading={isGenerating}
              disabled={!amount || parseInt(amount) < 100}
            >
              Continue to Payment
            </Button>
          </div>
        </div>

        {/* Footer Section */}
        <div className="payment-footer">
          <div className="footer-title">
            <Zap size={14} />
            <span>Quick Process</span>
          </div>
          <p className="footer-text">Your top-up will be processed within minutes</p>
        </div>
      </div>
    </div>
  );
}

export default PaymentDetails;
